import {create} from 'zustand';
import {overallEta, OverallEta, SpeedEstimator} from './upload-eta';

/**
 * One estimator per upload id, fed from every progress event (cheap, no
 * React state). The UI reads it on a shared 1-second tick so numbers don't
 * flicker and rows re-render at most once a second for speed/ETA.
 */
const estimators = new Map<string, SpeedEstimator>();
let lastSampleAt = 0;
let timer: ReturnType<typeof setInterval> | null = null;

export const TICK_MS = 1000;

export const useUploadEtaTick = create<{tick: number}>(() => ({tick: 0}));

function ensureTicker() {
  if (timer || typeof window === 'undefined') return;
  timer = setInterval(() => {
    useUploadEtaTick.setState(s => ({tick: s.tick + 1}));
    // Stop ticking a while after the last progress event (paused, done…).
    if (Date.now() - lastSampleAt > 8000 && timer) {
      clearInterval(timer);
      timer = null;
    }
  }, TICK_MS);
}

export function recordUploadProgress(id: string, bytesUploaded: number) {
  let estimator = estimators.get(id);
  if (!estimator) {
    estimator = new SpeedEstimator();
    estimators.set(id, estimator);
  }
  const now = Date.now();
  estimator.addSample(bytesUploaded, now);
  lastSampleAt = now;
  ensureTicker();
}

/** Pause, resume, offline, retry: the next events start a fresh estimate. */
export function resetUploadEta(id: string) {
  estimators.get(id)?.reset();
  ensureTicker();
}

export function forgetUploadEta(id: string) {
  estimators.delete(id);
}

export interface UploadEtaSnapshot {
  estimating: boolean;
  speed: number | null;
  seconds: number | null;
}

export function uploadEtaSnapshot(
  id: string,
  remainingBytes: number,
  now = Date.now(),
): UploadEtaSnapshot {
  const estimator = estimators.get(id);
  if (!estimator || estimator.isEstimating(now)) {
    return {estimating: true, speed: null, seconds: null};
  }
  const speed = estimator.speed(now);
  return {
    estimating: false,
    speed,
    seconds: estimator.eta(remainingBytes, now),
  };
}

interface EtaUploadLike {
  file: {id: string; size: number};
  status: string;
  bytesUploaded: number;
}

export function overallUploadEta(
  uploads: Iterable<EtaUploadLike>,
  now = Date.now(),
): OverallEta {
  const files = [];
  for (const upload of uploads) {
    const remaining = Math.max(0, upload.file.size - (upload.bytesUploaded || 0));
    if (upload.status === 'inProgress') {
      files.push({
        remaining,
        speed: uploadEtaSnapshot(upload.file.id, remaining, now).speed,
        active: true,
        queued: false,
      });
    } else if (upload.status === 'pending') {
      files.push({remaining, speed: null, active: false, queued: true});
    }
  }
  return overallEta(files);
}
